import type React from 'react';

import { type LabelDisplayedRowsArgs } from '@mui/material/TablePagination/TablePagination';

export interface StandardTableProps<T extends RowData> {
  id?: string;
  cellConfig?: CellConfigInterface;
  columns: ColumnData<T>[];
  customTableRowPreview?: (item: DragObjectItemInterface<T>) => React.ReactNode;
  customRowCellPreview?: (item: DragObjectItemInterface<T>) => React.ReactNode;
  data?: T[];
  onCellClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: T, cellIIndex?: number) => void;
  onHeaderCellClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowIndex?: number) => void;
  onCellContextMenu?: (event?: React.MouseEvent<any>, rowItem?: T, cellIIndex?: number) => void;
  onDragStart?(rowItem: T): void;
  onDrop?: (
    dragObject: DragObjectItemInterface<T>,
    endIndex: number,
    dropType: DropTypeEnum,
    dropRowId?: string,
  ) => unknown;
  onDragEnd?(): void;
  onRowClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: T, rowIndex?: number) => void;
  onRowMouseOver?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: T, rowIndex?: number) => void;
  onRowMouseOut?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: T, rowIndex?: number) => void;
  pageableConfig?: PageableConfigInterface;
  rowConfig?: RowConfigInterface;
  sortableConfig?: SortableConfigInterface;
  tableConfig?: TableConfigInterface;
  containerComponent?: any;
  clickable?: boolean;
  hover?: boolean;
  selectedRows?: string[];
}

export interface RowData {
  id: string;
  className?: string;
}
export interface ColumnData<T extends RowData> {
  key: string;
  getTitle?: () => { node: React.ReactNode; className?: string; headerClassName?: string };
  getValue: (t: T, cellIndex?: number) => { node: React.ReactNode; className?: string; isDragHandle?: boolean };
  hidden?: boolean;
  sortable?: boolean;
  clickable?: boolean;
  colSpan?: number;
  width?: string;
  bordered?: boolean;
}
export interface ClassNameInterface {
  className?: string;
}
export interface CellConfigInterface extends ClassNameInterface {
  draggableCell?: (rowItem: unknown, startIndex: number) => boolean;
  droppableCell?: (rowItem: unknown, startIndex: number) => boolean;
}
// TODO: add `droppableRow` property
export interface RowConfigInterface extends ClassNameInterface {
  draggableRow?: (rowItem: unknown, startIndex: number) => boolean;
  droppableRow?: (rowItem: unknown, startIndex: number) => boolean;
  selected?: boolean;
  isActive?: (item: unknown) => boolean;
}
export interface TableConfigInterface extends ClassNameInterface {
  tableHeight?: number;
}
export interface PageableConfigInterface {
  className?: string;
  count: number;
  labelNext?: string;
  labelDisplayedRows?: (paginationInfo: LabelDisplayedRowsArgs) => React.ReactNode;
  labelPrevious?: string;
  labelRowsPerPage?: string;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  page: number;
  rowsPerPageOptions: number[];
  rowsPerPage: number;
}
export type Order = 'asc' | 'desc' | '';
export interface SortableConfigInterface {
  order?: Order;
  orderBy?: string;
  sortIcons?: SortIconsInterface;
  onSort?: (property: string) => void;
}
export interface SortIconsInterface {
  asc: React.ReactNode;
  desc: React.ReactNode;
  none?: React.ReactNode;
}

export enum DropTypeEnum {
  ADD_TO_CELL = 'ADD_TO_CELL',
  REORDER_CELLS = 'REORDER_CELLS',
  REORDER_ROWS = 'REORDER_ROWS',
}
export interface DragObjectItemInterface<T> {
  item: T;
  startIndex: number;
}
